/**
 * ITBIS Visual - JavaScript utilities
 * ====================================
 * Provides client-side ITBIS visual price calculation for variant changes
 * and any dynamic price updates on the storefront.
 *
 * IMPORTANT: This does NOT modify cart, checkout, or real prices.
 * It only affects visual display in product pages, cards, and recommendations.
 *
 * Logic:
 * - If product has compare_at_price (sale): apply ITBIS to both compare_at and sale price.
 * - If product has no compare_at_price: apply ITBIS to selling price.
 */

(function() {
  'use strict';

  var settings = window.themeSettings || {};

  /**
   * Calculate visual ITBIS prices for a given variant.
   *
   * @param {Object} variant - Object with price and compare_at_price (in cents)
   * @param {Object} [overrideSettings] - Optional override for settings
   * @returns {Object} { displayPrice, displayCompareAtPrice, hasCompareAtPrice }
   */
  function getVisualItbisPrices(variant, overrideSettings) {
    var s = overrideSettings || settings;
    var hasCompareAtPrice = variant.compare_at_price &&
      variant.compare_at_price > variant.price;

    var isEnabled = s.enableVisualItbis;
    var rate = s.visualItbisRate || 18;

    var displayPrice = variant.price;
    var displayCompareAtPrice = variant.compare_at_price;

    if (isEnabled) {
      if (hasCompareAtPrice) {
        // Sale item: apply ITBIS to both compare_at and sale price
        // tax = Math.round(priceCents * 0.18), then total = price + tax
        displayCompareAtPrice = variant.compare_at_price + Math.round(variant.compare_at_price * rate / 100);
        displayPrice = variant.price + Math.round(variant.price * rate / 100);
      } else {
        // Regular item: apply ITBIS to selling price
        displayPrice = variant.price + Math.round(variant.price * rate / 100);
      }
    }

    return {
      displayPrice: displayPrice,
      displayCompareAtPrice: displayCompareAtPrice,
      hasCompareAtPrice: hasCompareAtPrice
    };
  }

  /**
   * Format a price in cents using the theme's money format.
   * Handles common Shopify money_format patterns.
   *
   * @param {Number} cents - Price in cents
   * @returns {String} Formatted price string
   */
  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    var amountNoDecimals = Math.floor(cents / 100).toString();
    var amountWithComma = amount.replace('.', ',');
    var amountNoDecimalsWithComma = amountNoDecimals;

    // Handle thousand separators
    var amountWithThousands = amountNoDecimals.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    var amountWithThousandsAndDecimals = amount.replace(/(\d)(?=(\d{3})+\.)/g, '$1,');

    var result = format
      .replace('{{amount_with_comma_separator}}', amountWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimalsWithComma)
      .replace('{{amount_with_apostrophe_separator}}', amount.replace(/(\d)(?=(\d{3})+\.)/g, "$1'"))
      .replace('{{amount_no_decimals}}', amountWithThousands)
      .replace('{{amount}}', amountWithThousandsAndDecimals);

    return result;
  }

  /**
   * Build the price HTML with ITBIS visual logic applied.
   *
   * @param {Object} variant - { price, compare_at_price } in cents
   * @returns {String} HTML string for the .prices container
   */
  function buildPriceHtml(variant) {
    var result = getVisualItbisPrices(variant);
    var html = '<div class="price" itemprop="offers" itemscope="" itemtype="https://schema.org/Offer">';

    if (result.hasCompareAtPrice) {
      html += '<span class="strike-through list">';
      html += '<span class="value" itemprop="price" content="' + (result.displayCompareAtPrice / 100).toFixed(2) + '">';
      html += formatMoney(result.displayCompareAtPrice);
      html += '</span></span>';
      html += '<span class="sales">';
      html += '<span class="value" itemprop="lowprice" content="' + (result.displayPrice / 100).toFixed(2) + '">';
      html += formatMoney(result.displayPrice);
      html += '</span></span>';
    } else {
      html += '<span class="sales">';
      html += '<span class="value" itemprop="price" content="' + (result.displayPrice / 100).toFixed(2) + '">';
      html += formatMoney(result.displayPrice);
      html += '</span></span>';
    }

    html += '</div>';
    return html;
  }

  // Expose globally for use by other scripts
  window.ItbisVisual = {
    getVisualItbisPrices: getVisualItbisPrices,
    formatMoney: formatMoney,
    buildPriceHtml: buildPriceHtml
  };

})();
